import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { App, Button, Card, Input, Modal, Spin } from "antd";
import {
  CloseOutlined,
  EditOutlined,
  InboxOutlined,
  LeftOutlined,
  MessageOutlined,
  PlayCircleOutlined,
  SendOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import dayjs from "dayjs";
import theme from "../../themes";
import {
  pageShellCss,
  pageTitleCss,
  wrapRowCss,
} from "../../styles/responsive";
import { useAppDispatch } from "../../store";
import { updateCampaignThunk } from "../../store/actions/campaign.action";
import {
  createTestMessage,
  getCampaignById,
} from "../../services/campaign.service";
import { ReactComponent as StopCampaignIcon } from "../../assets/icons/stopCampaign.svg";
import { ReactComponent as CheckCircleIcon } from "../../assets/icons/checkCircle.svg";
import { IUpdateCampaign } from "../../models/campaign.model";
import ToastMessage from "../../utils/toastMessage";
import { formatNumber } from "../../utils/formatDecimalNumber";
import ScheduleCampaignModal from "../../components/ScheduleCampaignModal";
import { TIMEZONE_CONFIG, TimezoneKey } from "../../utils/timezoneUtils";
import {
  CAMPAIGN_STATUS,
  isMessagingCampaignChannel,
  normalizeCampaignChannel,
} from "./type";
import { extractTemplateVariableNames } from "../../utils/templateVariables";

const POLL_MS = 8000;

const statusColors: Record<string, { text: string; background: string }> = {
  [CAMPAIGN_STATUS.IN_PROGRESS]: {
    text: theme.colors.green1,
    background: theme.colors.green2,
  },
  [CAMPAIGN_STATUS.TEST]: {
    text: theme.colors.yellow2,
    background: theme.colors.yellow3,
  },
  [CAMPAIGN_STATUS.SCHEDULE]: {
    text: theme.colors.blue1,
    background: theme.colors.blue2,
  },
  [CAMPAIGN_STATUS.COMPLETED]: {
    text: theme.colors.purple1,
    background: theme.colors.purple2,
  },
  [CAMPAIGN_STATUS.STOPPED]: {
    text: theme.colors.grey1,
    background: theme.colors.grey5,
  },
};

const greetingOf = (campaign: any): string =>
  String(
    campaign?.greeting_message ||
      campaign?.agent_id?.voice_configs?.greeting_message ||
      campaign?.agent_id?.greeting_message ||
      ""
  ).trim();

const customVariableNames = (campaign: any): string[] => {
  const fromMessage = extractTemplateVariableNames(greetingOf(campaign));
  if (fromMessage.length) return fromMessage;
  const raw = campaign?.agent_id?.parameters?.custom_variables;
  if (!raw) return [];
  return Object.values(raw).filter(
    (varName: unknown): varName is string => typeof varName === "string"
  );
};

type MessageCampaignDetailProps = {
  initialCampaign?: any;
};

const MessageCampaignDetail: React.FC<MessageCampaignDetailProps> = ({
  initialCampaign,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const { message } = App.useApp();
  const toast = ToastMessage(message);

  const [campaign, setCampaign] = useState<any>(initialCampaign || null);
  const [loading, setLoading] = useState(!initialCampaign);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isStopModalOpen, setIsStopModalOpen] = useState(false);
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [testRecipientName, setTestRecipientName] = useState("");
  const [testPhoneNumber, setTestPhoneNumber] = useState("");
  const [testCustomVars, setTestCustomVars] = useState<Record<string, string>>(
    {}
  );
  const [testErrors, setTestErrors] = useState<{
    recipientName?: string;
    phoneNumber?: string;
    customVars?: Record<string, string | undefined>;
  }>({});

  const channel = normalizeCampaignChannel(campaign?.channel);
  const isWhatsApp = channel === "whatsapp";
  const channelLabel = isWhatsApp
    ? t("campaignDetail.channelWhatsapp")
    : t("campaignDetail.channelSms");

  const testSchema = yup.object().shape({
    recipientName: yup
      .string()
      .required(t("campaignDetail.validation.recipientNameRequired")),
    phoneNumber: yup
      .string()
      .required(t("campaignDetail.validation.phoneNumberRequired"))
      .matches(
        /^[0-9\-+() ]+$/,
        t("campaignDetail.validation.invalidPhoneNumber")
      ),
    customVars: yup.object().test(
      "custom-vars-validation",
      t("campaignDetail.validation.customVariablesRequired"),
      function (value: any) {
        const names = customVariableNames(campaign);
        return names.every(
          (varName) => value && value[varName] && String(value[varName]).trim()
        );
      }
    ),
  });

  const fetchCampaignDetail = useCallback(
    async (silent = false) => {
      if (!id) return;
      try {
        if (!silent) setLoading(true);
        const res = await getCampaignById(id);
        setCampaign(res.data?.data || null);
      } catch (error) {
        console.error(error);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [id]
  );

  useEffect(() => {
    setCampaign(initialCampaign || null);
    setLoading(!initialCampaign);
    fetchCampaignDetail(Boolean(initialCampaign));
  }, [fetchCampaignDetail, initialCampaign, id]);

  useEffect(() => {
    const names = customVariableNames(campaign);
    setTestCustomVars((prev) => {
      const next: Record<string, string> = {};
      names.forEach((name) => {
        next[name] = prev[name] || "";
      });
      return next;
    });
  }, [campaign]);

  useEffect(() => {
    if (campaign?.status !== CAMPAIGN_STATUS.IN_PROGRESS) return undefined;
    const timer = window.setInterval(() => {
      fetchCampaignDetail(true);
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [campaign?.status, fetchCampaignDetail]);

  const stats = campaign?.stats || {};
  const sent = Number(stats.number_of_sent || 0);
  const replied = Number(stats.number_of_replied || 0);
  const failed = Number(stats.number_of_failed || 0);
  const pending = Number(stats.number_of_pending || 0);
  const totalRecipients = Number(
    stats.total_recipient || campaign?.number_of_recipients || 0
  );
  const replyRate = sent
    ? Math.min(100, (replied / sent) * 100)
    : Number(stats.reply_rate || 0) * 100;
  const sendProgress = useMemo(() => {
    if (!totalRecipients) return 0;
    return Math.min(100, (sent / totalRecipients) * 100);
  }, [sent, totalRecipients]);

  const getStatusLabel = useCallback(
    (status: string) => {
      if (!status) return t("outbound.statusLabels.allStatus");
      const statusMap: Record<string, string> = {
        [CAMPAIGN_STATUS.ALL]: t("outbound.statusLabels.allStatus"),
        [CAMPAIGN_STATUS.SCHEDULE]: t("outbound.statusLabels.scheduled"),
        [CAMPAIGN_STATUS.IN_PROGRESS]: t("outbound.statusLabels.inProgress"),
        [CAMPAIGN_STATUS.STOPPED]: t("outbound.statusLabels.stopped"),
        [CAMPAIGN_STATUS.COMPLETED]: t("outbound.statusLabels.finished"),
        [CAMPAIGN_STATUS.TEST]: t("outbound.statusLabels.testing"),
      };
      return statusMap[status] || status;
    },
    [t]
  );

  const formattedSchedule = (() => {
    if (!campaign?.start_at) return "-";
    const zone =
      TIMEZONE_CONFIG[campaign?.time_zone as TimezoneKey] ||
      campaign?.time_zone;
    const parsed = zone
      ? dayjs.utc(campaign.start_at).tz(zone)
      : dayjs.utc(campaign.start_at);
    return parsed.isValid() ? parsed.format("MMM DD, YYYY hh:mm A") : "-";
  })();

  const greeting = greetingOf(campaign);

  const handleUpdateCampaign = async (payload: IUpdateCampaign) => {
    await dispatch(updateCampaignThunk({ id: id || "", payload })).unwrap();
    await fetchCampaignDetail(true);
  };

  const resetTestForm = () => {
    setTestRecipientName("");
    setTestPhoneNumber("");
    setTestCustomVars((prev) => {
      const next: Record<string, string> = {};
      Object.keys(prev).forEach((key) => {
        next[key] = "";
      });
      return next;
    });
    setTestErrors({});
  };

  const handleSendTest = async () => {
    try {
      await testSchema.validate(
        {
          recipientName: testRecipientName,
          phoneNumber: testPhoneNumber,
          customVars: testCustomVars,
        },
        { abortEarly: false }
      );
      setIsSendingTest(true);
      await createTestMessage({
        campaign_id: id || "",
        to: testPhoneNumber,
        recipient_name: testRecipientName,
        variables: testCustomVars,
      });
      setTestErrors({});
      setIsTestModalOpen(true);
      toast.success(
        t("campaignDetail.testMessageSent"),
        5,
        <CheckCircleIcon />
      );
    } catch (err: any) {
      if (err.inner) {
        const errors: {
          recipientName?: string;
          phoneNumber?: string;
          customVars?: Record<string, string>;
        } = {};
        err.inner.forEach((e: any) => {
          if (e.path === "customVars") {
            const customVarErrors: Record<string, string> = {};
            customVariableNames(campaign).forEach((varName) => {
              if (!testCustomVars[varName] || !testCustomVars[varName].trim()) {
                customVarErrors[varName] = t(
                  "campaignDetail.validation.variableRequired",
                  { variableName: varName }
                );
              }
            });
            errors.customVars = customVarErrors;
          } else if (e.path) {
            errors[e.path as keyof typeof errors] = e.message;
          }
        });
        setTestErrors(errors);
        return;
      }
      toast.error(
        err?.response?.data?.message || t("campaignDetail.testMessageFailed"),
        5
      );
    } finally {
      setIsSendingTest(false);
    }
  };

  const goToInbox = () => {
    const params = new URLSearchParams({ campaign_id: id || "" });
    if (channel) params.set("channel", channel);
    navigate(`/inbound?${params.toString()}`);
  };

  const renderActions = () => {
    if (!campaign) return null;
    const status = String(campaign.status || "").toLowerCase();
    if (
      status === CAMPAIGN_STATUS.STOPPED ||
      status === CAMPAIGN_STATUS.COMPLETED
    ) {
      return (
        <ActionRow>
          <Button size="large" type="default" onClick={goToInbox}>
            <InboxOutlined />
            {t("campaignDetail.viewReplies")}
          </Button>
          <Button
            size="large"
            type="default"
            onClick={() =>
              navigate(`/reports/${campaign._id}`, {
                state: {
                  campaignName: campaign.campaign_name,
                  channel: campaign.channel,
                },
              })
            }
          >
            {t("campaignDetail.viewReport")}
          </Button>
        </ActionRow>
      );
    }
    if (status === CAMPAIGN_STATUS.TEST) {
      return (
        <Button
          size="large"
          type="default"
          icon={<EditOutlined style={{ marginRight: 4 }} />}
          onClick={() => setIsScheduleModalOpen(true)}
        >
          {t("campaignDetail.editScheduleTime")}
        </Button>
      );
    }
    if (status === CAMPAIGN_STATUS.SCHEDULE) {
      return (
        <ActionRow>
          <Button
            size="large"
            type="default"
            icon={<EditOutlined style={{ marginRight: 4 }} />}
            onClick={() => setIsScheduleModalOpen(true)}
          >
            {t("campaignDetail.editScheduleTime")}
          </Button>
          <Button
            size="large"
            type="default"
            icon={<PlayCircleOutlined style={{ marginRight: 4 }} />}
            onClick={async () => {
              await handleUpdateCampaign({
                status: CAMPAIGN_STATUS.IN_PROGRESS,
              });
              toast.success(
                t("campaignDetail.campaignStartedSuccessfully"),
                5,
                <CheckCircleIcon />
              );
            }}
          >
            {t("campaignDetail.startCampaign")}
          </Button>
        </ActionRow>
      );
    }
    if (status === CAMPAIGN_STATUS.IN_PROGRESS) {
      return (
        <ActionRow>
          <Button size="large" type="default" onClick={goToInbox}>
            <InboxOutlined />
            {t("campaignDetail.viewReplies")}
          </Button>
          <Button
            size="large"
            type="default"
            icon={<StopCampaignIcon style={{ marginRight: 4 }} />}
            onClick={() => setIsStopModalOpen(true)}
          >
            {t("campaignDetail.stopCampaign")}
          </Button>
        </ActionRow>
      );
    }
    return null;
  };

  return (
    <FlexRow>
      {loading && !campaign && (
        <LoadingOverlay>
          <Spin size="default" />
        </LoadingOverlay>
      )}
      {!loading && !campaign ? (
        <MainContent>
          <BackLink onClick={() => navigate("/outbound")}>
            <LeftOutlined style={{ marginRight: 8 }} />{" "}
            {t("campaignDetail.backToCampaigns")}
          </BackLink>
          <DetailsCard>
            <DetailsTitle>
              {t("campaignDetail.details.campaignDetails")}
            </DetailsTitle>
            <DetailValue>{t("common.noData")}</DetailValue>
          </DetailsCard>
        </MainContent>
      ) : null}
      {campaign ? (
        <MainContent>
          <BackLink onClick={() => navigate("/outbound")}>
            <LeftOutlined style={{ marginRight: 8 }} />{" "}
            {t("campaignDetail.backToCampaigns")}
          </BackLink>

          <HeaderRow>
            <Title>
              {campaign?.campaign_name ||
                t("campaignDetail.details.campaignDetails")}
              {isMessagingCampaignChannel(campaign?.channel) && (
                <ChannelBadge $whatsapp={isWhatsApp}>
                  {isWhatsApp ? <WhatsAppOutlined /> : <MessageOutlined />}
                  {channelLabel}
                </ChannelBadge>
              )}
              {campaign && (
                <StatusBadge
                  colors={
                    statusColors[campaign.status] || {
                      text: "#374151",
                      background: "#f3f4f6",
                    }
                  }
                >
                  {getStatusLabel(campaign.status)}
                </StatusBadge>
              )}
            </Title>
            {renderActions()}
          </HeaderRow>

          {campaign &&
            campaign.status !== CAMPAIGN_STATUS.TEST &&
            campaign.status !== CAMPAIGN_STATUS.SCHEDULE && (
              <CardFlexRow>
                <CardBoxCalls>
                  <CardTitle>{t("campaignDetail.messages")}</CardTitle>
                  <CallCount>
                    {sent}
                    <span style={{ color: "#000" }}>
                      {t("campaignDetail.sent")}
                    </span>
                    <span> / </span>
                    <span>{totalRecipients}</span>
                    <span>{t("campaignDetail.totalRecipients")}</span>
                  </CallCount>
                  <ProgressBar>
                    <ProgressFill style={{ width: `${sendProgress}%` }} />
                  </ProgressBar>
                  {pending > 0 && (
                    <PendingHint>
                      {t("campaignDetail.stillSending", { count: pending })}
                    </PendingHint>
                  )}
                </CardBoxCalls>
                <CardBoxStats>
                  <CardTitleStats>
                    {t("campaignDetail.statistics")}
                  </CardTitleStats>
                  <StatGrid>
                    <StatItem>
                      <StatValue>{replied}</StatValue>
                      <StatLabel>{t("campaignDetail.replied")}</StatLabel>
                    </StatItem>
                    <StatItem>
                      <StatValue>{formatNumber(replyRate)}%</StatValue>
                      <StatLabel>{t("campaignDetail.replyRate")}</StatLabel>
                    </StatItem>
                    <StatItem>
                      <StatValue>{failed}</StatValue>
                      <StatLabel>{t("campaignDetail.failed")}</StatLabel>
                    </StatItem>
                  </StatGrid>
                  <InboxLink type="button" onClick={goToInbox}>
                    <InboxOutlined />
                    {t("campaignDetail.viewReplies")}
                  </InboxLink>
                </CardBoxStats>
              </CardFlexRow>
            )}

          <DetailsCard>
            <DetailsTitle>
              {t("campaignDetail.details.campaignDetails")}
            </DetailsTitle>
            <DetailsGrid>
              <DetailLabel>{t("campaignDetail.details.channel")}</DetailLabel>
              <DetailValue>{channelLabel}</DetailValue>
              <DetailLabel>
                {t("campaignDetail.details.phoneNumber")}
              </DetailLabel>
              <DetailValue>{campaign?.phone_number || "-"}</DetailValue>
              {campaign?.agent_id?.agent_name ? (
                <>
                  <DetailLabel>{t("campaignDetail.details.agent")}</DetailLabel>
                  <DetailValue>
                    {campaign.agent_id.agent_name}
                  </DetailValue>
                </>
              ) : null}
              <DetailLabel>
                {t("campaignDetail.details.numberOfRecipients")}
              </DetailLabel>
              <DetailValue>
                {campaign?.number_of_recipients ?? totalRecipients ?? "-"}
              </DetailValue>
              <DetailLabel>
                {t("campaignDetail.details.recipientFile")}
              </DetailLabel>
              <DetailValue>
                {campaign?.audience_list_id ? (
                  <RecipientFileLink
                    href={campaign.audience_list_id.file}
                    download
                    rel="noopener noreferrer"
                  >
                    {campaign.audience_list_id.audience_list_name}
                  </RecipientFileLink>
                ) : (
                  "-"
                )}
              </DetailValue>
              <DetailLabel>
                {t("campaignDetail.details.scheduledTime")}
              </DetailLabel>
              <DetailValue>{formattedSchedule}</DetailValue>
              <DetailLabel>
                {t("campaignDetail.details.greetingMessage")}
              </DetailLabel>
              <DetailValue>
                {greeting ? (
                  <GreetingPreview>{greeting}</GreetingPreview>
                ) : (
                  t("campaignDetail.noGreeting")
                )}
              </DetailValue>
            </DetailsGrid>
          </DetailsCard>

          {campaign && campaign.status === CAMPAIGN_STATUS.TEST && (
            <TestCallCard>
              <TestCallTitle>{t("campaignDetail.testMessage")}</TestCallTitle>
              <TestCallGrid>
                <div>{t("campaignDetail.testRecipientName")}</div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <StyledAntdInput
                    allowClear
                    size="large"
                    placeholder={t("campaignDetail.testRecipientName")}
                    value={testRecipientName}
                    onChange={(e) => {
                      setTestRecipientName(e.target.value);
                      if (testErrors.recipientName) {
                        setTestErrors((prev) => ({
                          ...prev,
                          recipientName: undefined,
                        }));
                      }
                    }}
                    status={testErrors.recipientName ? "error" : ""}
                  />
                  {testErrors.recipientName && (
                    <div style={{ color: "red", fontSize: 13, marginTop: 2 }}>
                      {testErrors.recipientName}
                    </div>
                  )}
                </div>
                <div>{t("campaignDetail.testPhoneNumber")}</div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <StyledAntdInput
                    allowClear
                    size="large"
                    placeholder={t("campaignDetail.testPhoneNumber")}
                    value={testPhoneNumber}
                    onChange={(e) => {
                      setTestPhoneNumber(e.target.value);
                      if (testErrors.phoneNumber) {
                        setTestErrors((prev) => ({
                          ...prev,
                          phoneNumber: undefined,
                        }));
                      }
                    }}
                    status={testErrors.phoneNumber ? "error" : ""}
                  />
                  {testErrors.phoneNumber && (
                    <div style={{ color: "red", fontSize: 13, marginTop: 2 }}>
                      {testErrors.phoneNumber}
                    </div>
                  )}
                </div>
                {customVariableNames(campaign).map((customVar) => (
                  <div key={customVar} style={{ display: "contents" }}>
                    <div>{customVar}</div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <StyledAntdInput
                        allowClear
                        size="large"
                        placeholder={t("campaignDetail.enterVariable", {
                          variableName: customVar,
                        })}
                        value={testCustomVars[customVar] || ""}
                        onChange={(e) => {
                          setTestCustomVars((prev) => ({
                            ...prev,
                            [customVar]: e.target.value,
                          }));
                          if (testErrors.customVars?.[customVar]) {
                            setTestErrors((prev) => ({
                              ...prev,
                              customVars: {
                                ...(prev.customVars || {}),
                                [customVar]: undefined,
                              },
                            }));
                          }
                        }}
                        status={
                          testErrors.customVars?.[customVar] ? "error" : ""
                        }
                      />
                      {testErrors.customVars?.[customVar] && (
                        <div
                          style={{ color: "red", fontSize: 13, marginTop: 2 }}
                        >
                          {testErrors.customVars[customVar]}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </TestCallGrid>
              <div
                style={{
                  marginTop: 32,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <TestCallButton
                  type="primary"
                  onClick={handleSendTest}
                  loading={isSendingTest}
                  disabled={isSendingTest}
                >
                  <SendOutlined />
                  {t("campaignDetail.makeTestMessage")}
                </TestCallButton>
              </div>
            </TestCallCard>
          )}

          <Modal
            open={isStopModalOpen}
            onCancel={() => setIsStopModalOpen(false)}
            footer={null}
            width="min(400px, calc(100vw - 24px))"
            closeIcon={<CloseOutlined style={{ fontSize: 14, color: "#000" }} />}
            styles={{
              body: { display: "flex", flexDirection: "column", gap: 16 },
            }}
          >
            <div style={{ fontWeight: 600, fontSize: 16 }}>
              {t("campaignDetail.stopCampaign")}
            </div>
            <div style={{ fontSize: 16, fontWeight: 400 }}>
              {t("campaignDetail.areYouSureStopCampaign", {
                campaignName: campaign?.campaign_name,
              })}
              <br />
              {t("campaignDetail.onceStoppedCannotResume")}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
              <Button
                type="text"
                style={{
                  fontSize: 14,
                  fontWeight: 400,
                  border: "none",
                  background: "none",
                  boxShadow: "none",
                  color: "#000",
                  borderRadius: 12,
                }}
                size="middle"
                onClick={async () => {
                  await handleUpdateCampaign({
                    status: CAMPAIGN_STATUS.STOPPED,
                  });
                  toast.success(
                    t("campaignDetail.campaignStoppedSuccessfully"),
                    5,
                    <CheckCircleIcon />
                  );
                  setIsStopModalOpen(false);
                }}
              >
                {t("campaignDetail.stopAnyway")}
              </Button>
              <Button
                size="middle"
                style={{
                  fontSize: 14,
                  fontWeight: 400,
                  background: theme.colors.grey5,
                  color: "#000",
                  border: "none",
                  borderRadius: 8,
                  boxShadow: "none",
                }}
                onClick={() => setIsStopModalOpen(false)}
              >
                {t("campaignDetail.cancel")}
              </Button>
            </div>
          </Modal>

          <Modal
            open={isTestModalOpen}
            onCancel={() => {
              setIsTestModalOpen(false);
              resetTestForm();
            }}
            footer={null}
            width="min(520px, calc(100vw - 24px))"
            closeIcon={<CloseOutlined style={{ fontSize: 16, color: "#000" }} />}
            styles={{
              body: { display: "flex", flexDirection: "column", gap: 16 },
            }}
          >
            <div style={{ fontWeight: 600, fontSize: 18 }}>
              {t("campaignDetail.testMessage")}
            </div>
            <div style={{ fontSize: 16 }}>
              {t("campaignDetail.testMessageInProgress", {
                agentName: campaign?.agent_id?.agent_name || "Agent",
                phoneNumber: testPhoneNumber,
                channel: channelLabel,
              })}
            </div>
          </Modal>

          <ScheduleCampaignModal
            open={isScheduleModalOpen}
            onCancel={() => setIsScheduleModalOpen(false)}
            onSchedule={async (utcStartAt: string, tz: string) => {
              if (!utcStartAt) {
                toast.error(t("campaignDetail.pleaseSelectDateAndTime"));
                return;
              }
              try {
                await handleUpdateCampaign({
                  start_at: utcStartAt,
                  time_zone: tz,
                  status: CAMPAIGN_STATUS.SCHEDULE,
                });
                toast.success(
                  t("outbound.scheduleUpdatedSuccessfully"),
                  5,
                  <CheckCircleIcon />
                );
                setIsScheduleModalOpen(false);
              } catch (error) {
                toast.error(t("outbound.failedToUpdateSchedule"));
              }
            }}
          />
        </MainContent>
      ) : null}
    </FlexRow>
  );
};

const FlexRow = styled.div`
  display: flex;
  min-height: 100%;
  background-color: ${theme.colors.grey3};
`;

const MainContent = styled.div`
  flex: 1;
  min-width: 0;
  gap: 24px;
  display: flex;
  flex-direction: column;
  ${pageShellCss}
`;

const BackLink = styled.div`
  color: ${theme.colors.blue1};
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
`;

const HeaderRow = styled.div`
  ${wrapRowCss}
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  ${pageTitleCss}
`;

const StatusBadge = styled.span<{
  colors: { text: string; background: string };
}>`
  background: ${({ colors }) => colors.background};
  color: ${({ colors }) => colors.text};
  border-radius: 12px;
  padding: 4px 12px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
`;

const ChannelBadge = styled.span<{ $whatsapp: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${({ $whatsapp }) => ($whatsapp ? "#E8F8F0" : theme.colors.blue2)};
  color: ${({ $whatsapp }) => ($whatsapp ? "#128C7E" : theme.colors.blue1)};
  border-radius: 12px;
  padding: 4px 12px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const CardBox = styled(Card)`
  min-width: 0;
  border-radius: 12px;
  border: 1px solid ${theme.colors.grey2};
`;

const CardTitle = styled.div`
  font-size: 16px;
  color: ${theme.colors.grey1};
  margin-bottom: 16px;
`;

const CallCount = styled.div`
  font-size: 24px;
  margin-bottom: 8px;
  span {
    font-size: 16px;
    font-weight: 400;
    color: ${theme.colors.grey1};
    margin-left: 4px;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: ${theme.colors.grey2};
  border-radius: 8px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${theme.colors.green1};
  border-radius: 4px;
  transition: width 0.3s ease;
`;

const PendingHint = styled.div`
  margin-top: 10px;
  font-size: 13px;
  color: ${theme.colors.grey1};
`;

const StatGrid = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
  gap: 12px;
  flex-wrap: wrap;
`;

const StatItem = styled.div``;

const StatValue = styled.span`
  font-size: 24px;
`;

const StatLabel = styled.span`
  font-size: 16px;
  color: ${theme.colors.grey1};
  margin-left: 4px;
`;

const DetailsCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${theme.colors.grey2};
`;

const DetailsTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${theme.colors.grey1};
  margin-bottom: 24px;
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  row-gap: 24px;
  column-gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    row-gap: 8px;
  }
`;

const DetailLabel = styled.div`
  color: ${theme.colors.grey1};
  font-size: 15px;
  font-weight: 500;
`;

const DetailValue = styled.div`
  font-size: 15px;
  font-weight: 400;
  min-width: 0;
`;

const GreetingPreview = styled.div`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  background: ${theme.colors.grey3};
  border: 1px solid ${theme.colors.grey2};
  border-radius: 8px;
  padding: 12px 14px;
  max-height: 160px;
  overflow: auto;
  color: #111827;
`;

const CardFlexRow = styled.div`
  display: flex;
  gap: 24px;
  width: 100%;
  min-width: 0;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const CardBoxCalls = styled(CardBox)`
  flex: 1;
  min-width: 0;
`;

const CardBoxStats = styled(CardBox)`
  flex: 1;
  display: flex;
  flex-direction: column;

  .ant-card-body {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
  }
`;

const CardTitleStats = styled(CardTitle)`
  align-self: flex-start;
`;

const InboxLink = styled.button`
  margin-top: 16px;
  align-self: flex-start;
  border: none;
  background: none;
  padding: 0;
  color: ${theme.colors.blue1};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const RecipientFileLink = styled.a`
  color: ${theme.colors.blue1};
`;

const TestCallCard = styled(Card)`
  border-radius: 12px;
`;

const TestCallTitle = styled.div`
  font-size: 18px;
  font-weight: 500;
  color: #8c98a4;
  margin-bottom: 24px;
`;

const TestCallGrid = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  row-gap: 24px;
  column-gap: 24px;
  min-width: 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    row-gap: 8px;
  }
`;

const StyledAntdInput = styled(Input)`
  width: 320px;
  max-width: 100%;
  border-radius: 8px;
  font-size: 15px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const TestCallButton = styled(Button)`
  background: ${theme.colors.green1};
  border-radius: 8px;
  height: 40px;
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #fff;
  border: none;
  &:hover {
    background: ${theme.colors.green1} !important;
    color: #fff !important;
  }
`;

const LoadingOverlay = styled.div`
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  width: 100%;
`;

export default MessageCampaignDetail;
